import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@hooks/useTheme";
import { useTranslation } from "react-i18next";
import Breadcrumb from "@components/UI/Breadcrumb";
import { CustomInput, CustomTextarea } from "@components/Forms";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ComponentTable } from "@components/Tables";
import OutlineButton from "@components/Buttons/OutlineButton";
import SelectVerificationFilesForSbomMergeModal from "@components/UI/SelectVerificationFilesForSbomMergeModal";
import VerificationFileDetailsModal from "@components/Dialogs/VerificationFileDetailsModal";
import ProjectDetailsModal from "@components/Dialogs/ProjectDetailsModal";
import { DropIcon, PlusCircleIcon, DocumentInfoIcon, TrashIcon } from "@components/Icons";
import { PrimaryButton } from "@components/Buttons";
import useSbomMerge from "@hooks/useSbomMerge";
import { CHARACTER_LENGTH } from "@constants/index";
import { resolveVexScanToolCd } from "@constants/vex";
import { hasMissingComponentNames } from "@utils/common";

const MergedSbomInfoSection = ({ colors, formData, handleInputChange, errors, "data-testid": dataTestId = "add-sbom-merge-info-section" }) => {
  const { t } = useTranslation();

  return (
    <div data-testid={dataTestId}>
      <h2 className={`text-lg font-semibold ${colors.textPrimary} uppercase`} data-testid={`${dataTestId}-title`}>
        {t("project.sbom-merged.merged-sbom-information")}
      </h2>
      <p className={`mb-2 ${colors.textSubTitle} text-sm font-normal`} data-testid={`${dataTestId}-required-note`}>
        {t("project.required-fields-note")} <span className={`${colors.textRequired}`}>*</span>
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid={`${dataTestId}-grid`}>
        <div data-testid={`${dataTestId}-field-merged-name`} className="lg:col-span-2">
          <label className={`block text-sm font-medium ${colors.textSecondary} mb-2`}>
            {t("project.sbom-merged.merged-sbom-name")} <span className={`${colors.textRequired}`}>*</span>
          </label>
          <CustomInput
            type="text"
            value={formData.mergedName}
            onChange={(e) => handleInputChange("mergedName", e.target.value)}
            placeholder={t("project.sbom-merged.merged-sbom-name")}
            size="md"
            className={errors.mergedName ? `${colors.borderDangerOutLineButton}` : ""}
            maxLength={CHARACTER_LENGTH.TITLE}
          />
          {errors.mergedName && (
            <p className={`${colors.textDanger} text-sm mt-1`}>{ t(`project.sbom-merged.error-${errors.mergedName?.toLowerCase() || ''}`, errors.mergedName) }</p>
          )}
        </div>
        <div className="lg:col-span-2" data-testid={`${dataTestId}-field-copyright`}>
          <CustomTextarea
            value={formData.copyright}
            onChange={(e) => handleInputChange("copyright", e.target.value)}
            placeholder={t("project.sbom-merged.copyright")}
            rows={4}
            maxLength={CHARACTER_LENGTH.COMMENT}
          />
          <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={`${dataTestId}-field-copyright-char-count`}>
            {(formData.copyright || "").length}/{CHARACTER_LENGTH.COMMENT}
          </p>
        </div>
        <div className="lg:col-span-2" data-testid={`${dataTestId}-field-remark`}>
          <CustomTextarea
            value={formData.remark}
            onChange={(e) => handleInputChange("remark", e.target.value)}
            placeholder={t("project.sbom-merged.remark")}
            rows={4}
            maxLength={CHARACTER_LENGTH.REMARK}
          />
          <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid={`${dataTestId}-field-remark-char-count`}>
            {(formData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
          </p>
        </div>
      </div>
    </div>
  );
}

const VerificationFilesSection = ({
  t,
  colors,
  verificationFiles,
  handleAddVerificationFile,
  handleRemoveFile,
  handleProjectClick,
  errors,
  "data-testid": dataTestId = "add-sbom-merge-verification-files-section",
}) => {
  const [selectedFileForView, setSelectedFileForView] = useState(null);
  const [showFileDetailsModal, setShowFileDetailsModal] = useState(false);

  const handleViewFile = (file) => {
    setSelectedFileForView(file);
    setShowFileDetailsModal(true);
  };

  const handleCloseFileDetailsModal = () => {
    setShowFileDetailsModal(false);
    setSelectedFileForView(null);
  };
  const verificationFilesColumns = [
    "verificationVersion",
    "realFileName",
    "projectName",
    "verificationType",
    "action"
  ];

  const verificationFilesColumnConfig = {
    verificationVersion: {
      headerRender: () => t("project.verification.version"	),
      customRender: (value) => value || "-"
    },
    realFileName: {
      headerRender: () => t("project.verification.verification-file-name"),
      customRender: (value) => value || "-"
    },
    projectName: {
      headerRender: () => t("project.project-name"),
      customRender: (value, row) => (
        <span
          className={`cursor-pointer hover:underline ${colors.textActive}`}
          onClick={() => handleProjectClick(row.projectSeq)}
        >
          {value || "-"}
        </span>
      ),
    },
    verificationType: {
      headerRender: () => t("project.verification.verification-type"),
      customRender: (value) => value || "-"
    },
    action: {
      headerRender: () => t("common.action"),
      customRender: (value, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewFile(row)}
            className={`${colors.iconMuted} ${colors.hoverTextNeutral} p-1`}
            title={t("common.view-details")}
          >
            <DocumentInfoIcon size={20} />
          </button>
          <button
            onClick={() => handleRemoveFile(row)}
            className={`${colors.textDanger} ${colors.hoverTextDangerStrong} p-1`}
            title={t("common.delete-button")}
          >
            <TrashIcon size={20} />
          </button>
        </div>
      )
    }
  };

  return (
    <>
      <div data-testid={dataTestId}>
      <h2 className={`text-lg font-semibold ${colors.textPrimary} mb-2 uppercase`} data-testid={`${dataTestId}-title`}>
        {t("project.verification.verification-project-versions")}
        <span className={`${colors.textRequired}`}> *</span>
      </h2>
      <p className={`text-sm ${colors.textSecondary} mb-4`} data-testid={`${dataTestId}-description`}>
        {t("project.verification.select-verification-project-version")}
      </p>

      {verificationFiles.length === 0 ? (
        <div
          className={`${colors.bgVerifReportFile} ${errors.verificationFiles ? colors.borderDangerOutLineButton : colors.borderPrimary} border rounded-lg p-4`}
          data-testid={`${dataTestId}-empty`}
        >
          <div className="grid grid-cols-5 gap-4 mb-4 px-4 py-2">
            <div className={`${colors.textSecondary} text-sm font-medium`}>{t("common.version")}</div>
            <div className={`${colors.textSecondary} text-sm font-medium`}>{t("project.verification.verification-file-name")}</div>
            <div className={`${colors.textSecondary} text-sm font-medium`}>{t("project.project-name")}</div>
            <div className={`${colors.textSecondary} text-sm font-medium`}>{t("project.verification.verification-type")}</div>
            <div className={`${colors.textSecondary} text-sm font-medium`}>{t("common.action")}</div>
          </div>
          <div className="my-8 flex flex-col items-center justify-center space-y-4">
            <div className="flex items-center justify-center">
              <DropIcon size={60} className={`${colors.textActive}`}/>
            </div>
            <p className={`${colors.textSecondary} text-sm`}>
              {t("project.verification.no-verification-project-version-information")}
            </p>
          </div>

          <div className="flex justify-center mt-6">
            <PrimaryButton data-testid="add-sbom-merge-add-verification-file-button" onClick={handleAddVerificationFile}>
              {verificationFiles.length === 0 ? t("project.verification.add-verification-project-versions") : t("project.verification.change-verification-project-versions")}
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <>
          <div className={errors.verificationFiles ? `border ${colors.borderDangerOutLineButton} rounded-lg p-4` : ''} data-testid="add-sbom-merge-verification-files-table">
            <TableWithPagination
              columns={verificationFilesColumns}
              columnConfig={verificationFilesColumnConfig}
              data={verificationFiles}
              onRowClick={() => { }}
              loading={false}
              totalPages={1}
              totalData={verificationFiles.length}
              currentPage={1}
              setCurrentPage={() => { }}
              maxRowsPerPage={10}
              hasCheckbox={false}
              hasRadioButton={false}
              rowKey={["projectId", "verificationVersion", "verificationType"]}
              shouldShowGoToPage={false}
            />
          </div>
          {errors.verificationFiles && verificationFiles.length > 0 && (
            <p className={`${colors.textDanger} text-sm mt-2`}>{ t(`project.sbom-merged.error-${errors.verificationFiles?.toLowerCase() || ''}`, errors.verificationFiles) }</p>
          )}
          <div className="flex justify-center mt-6">
            <button
              type="button"
              data-testid={`${dataTestId}-add-button`}
              onClick={handleAddVerificationFile}
              className={`w-full inline-flex items-center justify-center px-6 py-2 border-2 border-dashed ${colors.borderOutLineButton} rounded-lg text-sm font-semibold ${colors.textActive} ${colors.bgAddVerificationFileBtn} transition-all duration-200`}
            >
              <PlusCircleIcon size={20} className="mr-2" />
              {t("project.verification.add-verification-project-version")}
            </button>
          </div>
        </>
      )}
      </div>

      {/* Verification File Details Modal */}
      <VerificationFileDetailsModal
        isOpen={showFileDetailsModal}
        onClose={handleCloseFileDetailsModal}
        file={selectedFileForView}
      />
    </>
  );
};

const AddSbomMerge = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitErrors, setSubmitErrors] = useState({});

  // Form data state
  const [formData, setFormData] = useState({
    mergedName: "",
    sbomSettings: "System Default SBOM",
    copyright: "",
    remark: ""
  });

  // Verification files state
  const [verificationFiles, setVerificationFiles] = useState([]);
  const [showSelectFilesModal, setShowSelectFilesModal] = useState(false);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);
  const [selectedProjectSeq, setSelectedProjectSeq] = useState(null);

  // Use the SBOM merge hook to fetch component data
  const { createSbomMerged, componentList, fetchComponentsForVerificationFiles } = useSbomMerge();


  const breadcrumbItems = [
    { label: t("project.sbom-merged.sbom-merge"), href: "/sbom-mergeds" },
    { label: t("project.sbom-merged.create-merged-sbom") },
  ];

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));  

    // Clear validation errors when user starts typing
    if (field === 'mergedName' && submitErrors.mergedName) {
      setErrors(prev => ({
        ...prev,
        mergedName: undefined
      }));
    }
  }, [submitErrors.mergedName]);

  const handleCancel = () => {
    navigate("/sbom-mergeds");
  };

  const handleSave = async () => {
    const newErrors = validateForm();

    // If there are errors, set them and return
    if (Object.keys(newErrors).length > 0) {
      setSubmitErrors(newErrors);
      return;
    }

    // Clear any existing errors
    setSubmitErrors({});

    // Create the payload
    const requestData = {
      mergedName: formData.mergedName,
      copyright: formData.copyright,
      remark: formData.remark,
      verificationFiles: verificationFiles.map(file => ({
        verificationType: file.verificationType,
        projectSeq: file.projectSeq,
        projectId: file.projectId,
        projectName: file.projectName,
        verificationVersion: file.verificationVersion,
        realFileName: file.realFileName
      })),
      mergedSbomComponents: componentList
    };

    await createSbomMerged.mutateAsync(requestData);
  };

  const handleAddVerificationFile = () => {
    setShowSelectFilesModal(true);
  };

  /** Rows come from different verifications — VEX must be scoped per row */
  const resolveRowVexContext = useCallback((row) => {
    const scanToolCd = resolveVexScanToolCd(row?.verificationType);
    if (!scanToolCd || row?.verificationSeq == null) return null;
    return { scanToolCd, verificationSeq: row.verificationSeq };
  }, []);

  const handleProjectClick = (projectSeq) => {
    setSelectedProjectSeq(projectSeq);
    setShowProjectDetailsModal(true);
  };

  const handleCloseProjectDetailsModal = () => {
    setShowProjectDetailsModal(false);
    setSelectedProjectSeq(null);
  };

  const handleSelectFilesModalClose = () => {
    setShowSelectFilesModal(false);
  };

  const handleSelectFilesModalAdd = (selectedFilesData) => {
    setVerificationFiles(selectedFilesData);
    setShowSelectFilesModal(false);

    // Clear verification files error when files are added
    if (submitErrors.verificationFiles) {
      setErrors(prev => ({
        ...prev,
        verificationFiles: undefined
      }));
    }

    // Fetch components immediately when files are selected
    if (selectedFilesData && selectedFilesData.length > 0) {
      fetchComponentsForVerificationFiles(selectedFilesData);
    }
  };

  const handleRemoveFile = useCallback((fileToRemove) => {
    const updatedFiles = verificationFiles.filter(file =>
      !(file.projectId === fileToRemove.projectId &&
        file.verificationVersion === fileToRemove.verificationVersion &&
        file.verificationType === fileToRemove.verificationType)
    );

    setVerificationFiles(updatedFiles);

    // Clear verification files error when files are removed
    if (submitErrors.verificationFiles) {
      setErrors(prev => ({
        ...prev,
        verificationFiles: undefined
      }));
    }

    // Fetch components for the updated file list
    if (updatedFiles.length > 0) {
      fetchComponentsForVerificationFiles(updatedFiles);
    } else {
      // If no files left, clear the component list
      fetchComponentsForVerificationFiles([]);
    }
  }, [verificationFiles, fetchComponentsForVerificationFiles, submitErrors.verificationFiles]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.mergedName.trim()) {
      errors.mergedName = t("project.sbom-merged.validation-name-msg");
    }

    if (verificationFiles.length < 2) {
      errors.verificationFiles = t(
        "project.sbom-merged.validation-two-project-versions-msg"
      );
    }

    if (hasMissingComponentNames(componentList)) {
      errors.componentNames = t(
        "project.sbom-merged.validation-component-name-msg"
      );
    }

    return errors;
  }, [formData.mergedName, verificationFiles.length, componentList, t]);

  const validationErrors = useMemo(
    () => validateForm(),
    [validateForm]
  );

  const isSaveDisabled = loading || Object.keys(validationErrors).length > 0;

  return (
    <div className={`min-h-full ${colors.bgSecondary}`} data-testid="add-sbom-merge-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`} data-testid="add-sbom-merge-header">
        <div className="flex items-center justify-between">
          <div className="items-center space-x-4">
            <Breadcrumb data-testid="add-sbom-merge-breadcrumb" items={breadcrumbItems} />
            <h1
              className={`${colors.textPrimary} text-2xl font-bold uppercase`}
              data-testid="add-sbom-merge-title"
            >
              {t("project.sbom-merged.create-merged-sbom")}
            </h1>
          </div>
          <div className="flex items-center space-x-3" data-testid="add-sbom-merge-actions">
            <OutlineButton data-testid="add-sbom-merge-cancel-button" onClick={handleCancel}>
              {t("common.cancel-button")}
            </OutlineButton>
            <PrimaryButton
              data-testid="add-sbom-merge-save-button"
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {t("common.save-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Form Container */}
      <div className="mt-5" data-testid="add-sbom-merge-form">
        <div className={`${colors.bgPrimary} rounded-lg border ${colors.borderPrimary} p-6`}>
          <div className="space-y-8">
            {/* Merged SBOM Information Section */}
            <MergedSbomInfoSection
              colors={colors}
              formData={formData}
              handleInputChange={handleInputChange}
              errors={submitErrors}
            />

            {/* Verification Files Section */}
            <VerificationFilesSection
              t={t}
              colors={colors}
              verificationFiles={verificationFiles}
              handleAddVerificationFile={handleAddVerificationFile}
              handleRemoveFile={handleRemoveFile}
              handleProjectClick={handleProjectClick}
              errors={submitErrors}
            />
            {submitErrors.verificationFiles && verificationFiles.length === 0 && (
              <p className={`${colors.textDanger} text-sm mt-2`}>{ t(`project.sbom-merged.error-${submitErrors.verificationFiles?.toLowerCase() || ''}`, submitErrors.verificationFiles) }</p>
            )}

            {/* Component List Section */}
            {componentList?.length > 0 && (
              <div data-testid="add-sbom-merge-component-list-section">
                <h2
                  className={`mb-3 text-lg font-semibold ${colors.textPrimary} uppercase`}
                  data-testid="add-sbom-merge-component-list-title"
                >
                  {t("common.component-list")} ({componentList?.length || 0})
                </h2>
                <div data-testid="add-sbom-merge-component-list-table">
                <ComponentTable
                  data={componentList || []}
                  licensePolicyData={[]}
                  isEditingAllowed={false}
                  seq="componentSeq"
                  isFossIDDependency={false}
                  maxRowsPerPage={10}
                  showPagination={true}
                  showIcon={false}
                  validationMode="export"
                  allowVexReview={true}
                  readOnlyVexReview={true}
                  resolveRowVexContext={resolveRowVexContext}
                  showShieldFilter
                />
                </div>
                {(validationErrors.componentNames || submitErrors.componentNames) && (
                  <p className={`${colors.textDanger} text-sm mt-2`}>
                    {validationErrors.componentNames || submitErrors.componentNames}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Select Verification Files Modal */}
      <SelectVerificationFilesForSbomMergeModal
        isOpen={showSelectFilesModal}
        onClose={handleSelectFilesModalClose}
        onAdd={handleSelectFilesModalAdd}
        initialSelectedFiles={verificationFiles}
      />

      {/* Project Details Modal */}
      <ProjectDetailsModal
        isOpen={showProjectDetailsModal}
        onClose={handleCloseProjectDetailsModal}
        projectSeq={selectedProjectSeq}
      />
    </div>
  );
};

export default AddSbomMerge;
